/**
 * Unified workspace snapshot load/save routing (SQLite vs local JSON vs git
 * YAML). Workspaces enrolled in sync_engine_v1 and migrated read/write the
 * Rust SQLite store as source of truth; the legacy file store is dual-written
 * after each save so a flag-off build (or the kill switch) still loads
 * everything — Phase 1's rollback lever, removed in Phase 8.
 */
import { isGitWorkspace } from '../utils/workspaceStorage.js';
import { loadSnapshot, saveSnapshot, normalizeSnapshot } from './localWorkspaceStore.js';
import {
  loadGitSnapshot,
  saveGitSnapshot,
  exportSnapshotToDirectory,
  isGitWorkspaceRoot,
  registerWorkspaceScope,
} from './gitWorkspaceStore.js';
import { ensureWorkspaceMigrated } from './workspaceDbMigration.js';
import { loadSqliteSnapshot, saveSqliteSnapshot } from './sqliteWorkspaceStore.js';
import { scheduleGitFlush } from './gitFlush.js';

export async function loadWorkspaceSnapshot(workspace) {
  if (await ensureWorkspaceMigrated(workspace)) {
    try {
      return await loadSqliteSnapshot(workspace);
    } catch (err) {
      console.warn('[sync] SQLite load failed — falling back to file store:', err);
    }
  }
  if (isGitWorkspace(workspace)) {
    return loadGitSnapshot(workspace.rootPath);
  }
  if (workspace?.id) {
    return loadSnapshot(workspace.id);
  }
  return normalizeSnapshot(null);
}

export async function saveWorkspaceSnapshot(workspace, partial, options = {}) {
  if (await ensureWorkspaceMigrated(workspace)) {
    try {
      await saveSqliteSnapshot(workspace, partial, options);
      dualWriteLegacy(workspace, partial, options);
      // Phase 4: the YAML tree (rootPath for git workspaces, the GitHub-sync
      // mirror otherwise) is produced from SQLite by the debounced git flush.
      // Phase 3's Rust engine will additionally emit sync:git-flush-needed;
      // that subscriber reuses the same flushWorkspaceToDisk entry point.
      scheduleGitFlush(workspace);
      return;
    } catch (err) {
      console.warn('[sync] SQLite save failed — falling back to file store:', err);
    }
  }
  if (isGitWorkspace(workspace)) {
    return saveGitSnapshot(workspace.rootPath, partial, options);
  }
  if (workspace?.id) {
    return saveSnapshot(workspace.id, partial, options);
  }
}

/**
 * Fire-and-forget legacy write after a successful SQLite save. Local JSON is
 * the rollback copy. Git workspaces no longer dual-write YAML here (Phase 4):
 * gitFlush owns serialization — it rewrites rootPath from SQLite after each
 * save, which doubles as the rollback copy for flag-off builds.
 */
function dualWriteLegacy(workspace, partial, options) {
  if (isGitWorkspace(workspace)) return;
  if (!workspace?.id) return;
  Promise.resolve(saveSnapshot(workspace.id, partial, options)).catch((err) => {
    console.warn('[sync] legacy dual-write failed (SQLite copy is authoritative):', err);
  });
}

export async function exportWorkspaceToDirectory(rootPath, snapshot, workspaceMeta) {
  return exportSnapshotToDirectory(rootPath, snapshot, workspaceMeta);
}

export async function openGitWorkspaceRoot(rootPath) {
  const root = String(rootPath || '').replace(/\\/g, '/').replace(/\/+$/, '');
  const valid = await isGitWorkspaceRoot(root);
  if (!valid) {
    throw new Error('Selected folder is not an Apiboo workspace (apiboo.yaml not found)');
  }
  await registerWorkspaceScope(root);
  const snap = await loadGitSnapshot(root);
  return {
    id: snap.id || root,
    local_uid: snap.id || root,
    name: snap.workspaceName || root.split('/').pop() || 'Git Workspace',
    type: 'personal',
    storage: 'git',
    source: 'personal',
    role: 'owner',
    rootPath: root,
    description: '',
    visibility: 'private',
    createdAt: Date.now(),
  };
}

export async function loadSnapshotById(workspaces, id) {
  const ws = (workspaces || []).find((w) => String(w.id) === String(id));
  if (ws) {
    return loadWorkspaceSnapshot(ws);
  }
  return loadSnapshot(id);
}

export async function saveSnapshotByWorkspace(workspace, partial, options = {}) {
  return saveWorkspaceSnapshot(workspace, partial, options);
}

export { loadGitSnapshot, saveGitSnapshot, exportSnapshotToDirectory };
